/**
 * Vue d'ensemble — Dépenses avec Engagements.
 * SCOPE STRICT : uniquement les contrats d'engagements récurrents fournisseurs.
 * (Le module Achats reste séparé — voir /app/purchases pour les factures fournisseurs.)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { Wallet, AlertTriangle, TrendingUp, FileText, Clock, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/engagements-categories";

const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €";
const URGENCE_COLORS = { depasse: "#dc2626", critique: "#ea580c", urgent: "#f59e0b", attention: "#eab308", ok: "#10b981" };
const CATEGORY_HEX = {
  pret: "#059669", credit: "#0d9488", assurance: "#2563eb", telephonie: "#9333ea",
  leasing: "#dc2626", saas: "#0891b2", energie: "#d97706", materiel: "#475569",
  maintenance: "#ea580c", location: "#4f46e5", services: "#db2777",
  abonnement: "#8b5cf6", autre: "#64748b",
};

export default function Depenses() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dr, cr] = await Promise.all([
          api.get("/engagements/dashboard"),
          api.get("/engagements/contrats"),
        ]);
        setDashboard(dr.data);
        setContrats(cr.data?.contrats || cr.data || []);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Chargement...</div>;
  if (!dashboard) return null;

  const actifs = contrats.filter(c => c.statut !== "resilier");
  const totalMensuel = actifs.reduce((s, c) => s + (c.montant_mensuel_ht || 0), 0);
  const totalAnnuel = actifs.reduce((s, c) => s + (c.montant_annuel_ht || c.montant_mensuel_ht * 12 || 0), 0);

  // Répartition par catégorie
  const byCategorie = {};
  actifs.forEach(c => {
    const k = c.categorie || "autre";
    byCategorie[k] = (byCategorie[k] || 0) + (c.montant_mensuel_ht || 0);
  });
  const catData = Object.entries(byCategorie)
    .map(([k, v]) => ({ name: CATEGORIES.find(c => c.value === k)?.label || k, value: Math.round(v * 100) / 100, cat: k }))
    .filter(x => x.value > 0).sort((a, b) => b.value - a.value);

  // Répartition par urgence préavis
  const urgenceCount = { depasse: 0, critique: 0, urgent: 0, attention: 0, ok: 0 };
  actifs.forEach(c => { const u = c.urgence || "ok"; if (urgenceCount[u] !== undefined) urgenceCount[u]++; });

  // Top 5 engagements
  const topEngagements = [...actifs]
    .filter(c => (c.montant_mensuel_ht || 0) > 0)
    .sort((a, b) => (b.montant_mensuel_ht || 0) - (a.montant_mensuel_ht || 0))
    .slice(0, 5)
    .map(c => ({ id: c.id, name: c.prestataire || c.nom_contrat, monthly: c.montant_mensuel_ht }));

  // Prochaines échéances de préavis (30j)
  const prochaines = actifs
    .filter(c => c.jours_restants !== null && c.jours_restants !== undefined && c.jours_restants <= 30 && c.jours_restants >= 0)
    .sort((a, b) => a.jours_restants - b.jours_restants)
    .slice(0, 5);

  // Trend 12 mois (coût mensuel projeté du portefeuille — plutôt stable)
  const now = new Date();
  const trend = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const t = actifs.filter(c => {
      const debut = c.date_debut || "";
      const fin = c.date_echeance || "9999-12-31";
      return debut <= (key + "-31") && fin >= (key + "-01");
    }).reduce((s, c) => s + (c.montant_mensuel_ht || 0), 0);
    trend.push({ period: key, engagements: Math.round(t * 100) / 100 });
  }

  return (
    <div className="space-y-6" data-testid="depenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Wallet className="w-6 h-6 text-violet-600" />Vue d'ensemble — Engagements
          </h1>
          <p className="text-sm text-slate-500">Pilotage de vos contrats de dépenses récurrentes fournisseurs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/app/depenses/import")} data-testid="btn-import">
            <FileText className="w-4 h-4 mr-2" />Importer (OCR IA)
          </Button>
          <Button onClick={() => navigate("/app/depenses/contrats/nouveau")} data-testid="btn-nouveau">
            <Plus className="w-4 h-4 mr-2" />Nouveau contrat
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50/40 to-transparent">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 uppercase mb-1"><Wallet className="w-3.5 h-3.5" />Coût mensuel</div>
              <p className="text-2xl font-bold font-mono text-violet-700" data-testid="kpi-mensuel">{fmt(totalMensuel)}</p>
              <p className="text-xs text-slate-500 mt-1">{actifs.length} contrat{actifs.length > 1 ? "s" : ""} actif{actifs.length > 1 ? "s" : ""}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 uppercase mb-1"><TrendingUp className="w-3.5 h-3.5" />Coût annuel</div>
              <p className="text-2xl font-bold font-mono text-slate-900" data-testid="kpi-annuel">{fmt(totalAnnuel)}</p>
              <p className="text-xs text-slate-500 mt-1">Projection sur 12 mois</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className={urgenceCount.depasse + urgenceCount.critique > 0 ? "border-red-200 bg-red-50/40" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 uppercase mb-1"><AlertTriangle className="w-3.5 h-3.5" />Préavis critiques</div>
              <p className={`text-2xl font-bold font-mono ${urgenceCount.depasse + urgenceCount.critique > 0 ? "text-red-600" : "text-slate-900"}`} data-testid="kpi-critiques">
                {urgenceCount.depasse + urgenceCount.critique}
              </p>
              <p className="text-xs text-slate-500 mt-1">{urgenceCount.depasse} dépassé · {urgenceCount.critique} ≤ 7j</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className={urgenceCount.urgent + urgenceCount.attention > 0 ? "border-amber-200 bg-amber-50/40" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 uppercase mb-1"><Clock className="w-3.5 h-3.5" />À surveiller</div>
              <p className={`text-2xl font-bold font-mono ${urgenceCount.urgent + urgenceCount.attention > 0 ? "text-amber-700" : "text-slate-900"}`} data-testid="kpi-surveiller">
                {urgenceCount.urgent + urgenceCount.attention}
              </p>
              <p className="text-xs text-slate-500 mt-1">{urgenceCount.urgent} ≤ 30j · {urgenceCount.attention} ≤ 60j</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Empty state global */}
      {actifs.length === 0 && (
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
          <CardContent className="text-center py-12">
            <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700 mb-1">Aucun contrat d'engagement</h3>
            <p className="text-sm text-slate-500 mb-4">Importez un premier contrat via l'OCR IA ou créez-le manuellement.</p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate("/app/depenses/import")}>
                <FileText className="w-4 h-4 mr-2" />Importer par OCR
              </Button>
              <Button onClick={() => navigate("/app/depenses/contrats/nouveau")}>
                <Plus className="w-4 h-4 mr-2" />Créer manuellement
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {actifs.length > 0 && (<>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Trend 6m */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Évolution du portefeuille d'engagements — 6 mois</CardTitle>
              <p className="text-xs text-slate-500">Coût mensuel de vos contrats actifs sur la période</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="period" stroke="#64748b" style={{ fontSize: "11px" }} />
                  <YAxis stroke="#64748b" style={{ fontSize: "11px" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Line type="monotone" dataKey="engagements" stroke="#8b5cf6" strokeWidth={2} name="Coût mensuel engagements" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Répartition par catégorie */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Répartition par catégorie</CardTitle></CardHeader>
            <CardContent>
              {catData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}`} labelLine={false} style={{ fontSize: "10px" }}>
                      {catData.map((entry, i) => (
                        <Cell key={i} fill={CATEGORY_HEX[entry.cat] || "#94a3b8"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-slate-400 text-center py-8">Aucune catégorie</p>}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top 5 engagements */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Wallet className="w-4 h-4 text-violet-600" />Top 5 engagements du mois</CardTitle>
              <p className="text-xs text-slate-500">Vos contrats récurrents les plus coûteux (mensualisés)</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={topEngagements} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#64748b" style={{ fontSize: "11px" }} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
                  <YAxis type="category" dataKey="name" stroke="#64748b" style={{ fontSize: "11px" }} width={120} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="monthly" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Prochaines échéances préavis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Prochains préavis (≤ 30j)</CardTitle>
              <p className="text-xs text-slate-500">Contrats nécessitant une action rapide</p>
            </CardHeader>
            <CardContent>
              {prochaines.length > 0 ? (
                <div className="space-y-2">
                  {prochaines.map(c => (
                    <button key={c.id} onClick={() => navigate(`/app/depenses/contrats/${c.id}`)}
                                data-testid={`prochain-${c.id}`}
                                className="w-full flex items-center gap-3 p-2.5 rounded-md border border-slate-200 hover:bg-slate-50 text-left">
                      <div className="w-2 h-8 rounded" style={{ background: URGENCE_COLORS[c.urgence] }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.nom_contrat}</p>
                        <p className="text-[11px] text-slate-500 truncate">{c.prestataire}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: URGENCE_COLORS[c.urgence], color: URGENCE_COLORS[c.urgence] }}>
                        J-{c.jours_restants}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Aucun préavis urgent — tout est sous contrôle 👌</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </>)}
    </div>
  );
}
